import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, ArrowRight, RefreshCw, CheckCircle2, Plus, Sparkles, Loader2 } from "lucide-react";

export default function Homework() {
  const [items, setItems] = useState([]);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicQuery, setTopicQuery] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [mix, setMix] = useState("both");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/homework"), api.get("/homework/sets")]).then(([a, b]) => {
      setItems(a.data.items || []);
      setSets(b.data || []);
      setLoading(false);
    });
  };
  useEffect(load, []);

  const openAssign = async () => {
    setShowAssign(true);
    if (topics.length === 0) {
      const r = await api.get("/topics");
      setTopics(r.data);
    }
  };

  const assign = async (topic) => {
    setAssigning(true);
    try {
      await api.post("/homework/generate", { topic_id: topic.id, count: 20, mix });
      setShowAssign(false);
      load();
    } finally { setAssigning(false); }
  };

  const filtered = topics.filter(t => t.name.toLowerCase().includes(topicQuery.toLowerCase())).slice(0, 30);

  return (
    <div data-testid="homework-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-bold mb-2">Today's Homework</h1>
          <p className="text-lg" style={{ color: "var(--text-secondary)" }}>
            Spaced-repetition queue — the topics due for review today, weakest first.
          </p>
        </div>
        <button onClick={openAssign} className="pm-btn-primary flex items-center gap-2 text-sm" data-testid="hw-assign-new-btn">
          <Plus size={14} /> Assign new
        </button>
      </div>

      {/* Assigned sets */}
      {sets.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-2xl font-bold mb-4">Your homework sets</h2>
          <div className="grid md:grid-cols-2 gap-3" data-testid="hw-sets-list">
            {sets.slice(0, 8).map((s, i) => (
              <Link key={s.id} to={`/app/questions?topic=${s.topic_id}`} className="pm-card pm-card-hover p-4 flex items-center justify-between" data-testid={`hw-set-${i}`}>
                <div>
                  <div className="font-medium">{s.topic_name}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{s.question_ids?.length || 0} questions · {s.mix}</div>
                </div>
                <ArrowRight size={16} style={{ color: "var(--text-muted)" }} />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Assign modal */}
      {showAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={() => setShowAssign(false)} data-testid="hw-assign-modal">
          <div className="pm-card p-6 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl font-bold mb-2">Assign homework</div>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>AI-generates 20 questions for the topic you pick.</p>
            <div className="flex gap-2 mb-3">
              {["content","application","both"].map(m => (
                <button key={m} onClick={() => setMix(m)} className="px-3 py-1 rounded-full text-xs font-medium capitalize" style={{ background: mix === m ? "var(--primary)" : "var(--muted)", color: mix === m ? "white" : "var(--text)" }} data-testid={`hw-mix-${m}`}>{m}</button>
              ))}
            </div>
            <input value={topicQuery} onChange={(e) => setTopicQuery(e.target.value)} placeholder="Search topics…" className="pm-input mb-3" data-testid="hw-topic-search" />
            <div className="overflow-y-auto flex-1 space-y-1" data-testid="hw-topic-results">
              {filtered.map(t => (
                <button key={t.id} onClick={() => assign(t)} disabled={assigning} className="w-full text-left p-2 rounded-lg hover:bg-[var(--muted)] text-sm flex items-center justify-between" data-testid={`hw-assign-${t.id}`}>
                  <span>{t.name}</span>
                  {assigning ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={12} style={{ color: "var(--accent)" }} />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="pm-card p-8 text-center" data-testid="hw-empty">
          <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: "var(--mastery)" }} />
          <div className="font-display text-2xl font-bold mb-2">All caught up!</div>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            No reviews due today. Study new material to fill your queue.
          </p>
          <Link to="/app/subjects" className="pm-btn-primary" data-testid="hw-explore-btn">Explore subjects</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="hw-list">
          {items.map((h, i) => (
            <div key={i} className="pm-card p-5" data-testid={`hw-item-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="pm-badge" style={{ background: h.accuracy < 0.5 ? "#fdecea" : "var(--hl-yellow)", color: h.accuracy < 0.5 ? "var(--weakness)" : "var(--text)" }}>
                  {h.accuracy < 0.5 ? "Weak" : "Review"}
                </div>
                <div className="text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                  <Clock size={11} /> {Math.round(h.accuracy * 100)}% acc
                </div>
              </div>
              <div className="font-display font-bold text-lg mb-3">{h.topic.name}</div>
              <div className="flex gap-2">
                <Link to={`/app/topic/${h.topic.id}`} className="pm-btn-secondary text-sm flex-1 text-center" data-testid={`hw-review-btn-${i}`}>
                  Re-read
                </Link>
                <Link to={`/app/questions?topic=${h.topic.id}`} className="pm-btn-primary text-sm flex-1 text-center flex items-center justify-center gap-1" data-testid={`hw-quiz-btn-${i}`}>
                  Quiz me <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
